import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import * as Y from 'yjs';
import { Flex, Drawer, LogoAnimated, Text, ToolPanel } from '@nula/core-ui';
import { useEntityYjs } from '@/lib/sync/use-entity-yjs';
import type { YjsRoomKind } from '@/lib/sync/yjs-registry';
import { EmbeddableCanvas } from '@/features/canvas/components/embeddable-canvas';
import { FramesPanel } from '@/features/canvas/engine/surfaces/frames-panel';
import { ImperativeProvider } from '@/features/canvas/engine/wiring/imperative';
import { createNode } from '@/features/canvas/engine/primitives/persistence/create-node';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import { absoluteToFrameLocal } from '@/features/canvas/engine/primitives/geometry/geometry';
import { NodeActionsContext } from '@/features/canvas/engine/wiring/contexts/node-actions-context';
import { useNodeActionsImpl } from './hooks/use-node-actions-impl';
import { CanvasToolbar } from './toolbar/canvas-toolbar';
import type { ToolPanelConfig } from './toolbar/canvas-toolbar';
import { useCanvasTool } from './toolbar/use-canvas-tool';
import type { ActiveTool } from './toolbar/use-canvas-tool';
import { ToolPlacementGhost } from './tool-placement-ghost';
import { resolvePlacementSize } from './placement-size';
import { HIGHLIGHTER_OPACITY } from '@/features/canvas/utils/constants';
import { useAuthStore } from '@/stores/use-auth-store';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { CanvasAwarenessMount } from './canvas-awareness-mount';
import { usePointerMode } from '@/features/canvas/engine/hooks/use-pointer-mode';
import { CanvasContext } from './canvas-context';
import { useTentativeBatch } from './hooks/use-tentative-batch';
import { TentativeBatchToast } from './tentative-batch-toast';
import { useResolverOnPaste } from './hooks/use-resolver-on-paste';
import { usePasteHandler } from './hooks/use-paste-handler';
import { useToolShortcut } from './hooks/shortcuts/use-tool-shortcut';
import { useSpacePanShortcut } from './hooks/shortcuts/use-space-pan-shortcut';
import { useShortcutsPanelShortcut } from './hooks/shortcuts/use-shortcuts-panel-shortcut';
import { CanvasShortcutsPanel } from './canvas-shortcuts-panel';
import { Actionbar } from '@/components/actionbar/actionbar';
import styles from './canvas-document-mount.module.css';

interface CanvasEditorProps {
  entityId: string;
  /** Default 'canvas'. Pass 'space' from SpaceMount when mounting Space's canvas_state. */
  entityKind?: YjsRoomKind;
  /** When true, disables all editing — no toolbar, no paste, no node creation. */
  readOnly?: boolean;
  /**
   * When true, this editor is embedded inside a host surface (e.g. a document
   * surface's Diagram tab) that already provides page-level chrome. Suppresses the
   * editor's OWN standalone chrome — the Actionbar and the "skip to canvas
   * content" link — to avoid a duplicate. Editing (toolbar, engine) is
   * unaffected; `embedded` is orthogonal to `readOnly`.
   */
  embedded?: boolean;
}

export function CanvasEditor({
  entityId,
  entityKind = 'canvas',
  readOnly = false,
  embedded = false,
}: CanvasEditorProps) {
  const { doc, awareness, synced } = useEntityYjs(entityId, entityKind);
  const binding = useCanvasBinding();
  const user = useAuthStore((s) => s.user);
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId) ?? '';
  const { activeTool, selectTool, reset } = useCanvasTool();
  const spacePreToolRef = useRef<typeof activeTool | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const handleViewportChange = useCallback((v: { x: number; y: number; zoom: number }) => {
    setZoom(v.zoom);
  }, []);

  // Subscribe to nodes Map mutations so the empty-hint reactively hides/shows
  // as the canvas mutates. useMemo is wrong here — it doesn't re-run when Yjs
  // emits a change event; it only re-runs when its deps change.
  const [isEmpty, setIsEmpty] = useState<boolean>(true);
  useEffect(() => {
    if (!doc) return;
    const nodes = canvasRoots(doc as Y.Doc, binding).nodes;
    const update = () => setIsEmpty(nodes.size === 0);
    update();
    nodes.observe(update);
    return () => nodes.unobserve(update);
  }, [doc, binding]);

  // Stable empty doc for hook calls before the real doc is available.
  // useTentativeBatch/useResolverOnPaste subscribe to Yjs events — they must
  // be called unconditionally per the Rules of Hooks.
  const emptyDoc = useMemo(() => new Y.Doc(), []);
  const { count: tentativeCount, acceptAll } = useTentativeBatch({
    doc: doc ?? emptyDoc,
    clientId: doc?.clientID ?? 0,
  });

  // Paste handler: resolves plain-text paste against entity resolver,
  // or falls back to canvas-clipboard deserialization for MIME-typed content.
  const handlePastedText = useResolverOnPaste({
    doc: doc ?? emptyDoc,
    clientId: doc?.clientID ?? 0,
    workspaceId,
  });

  // Frames-panel open/close — local state, no global store. The
  // panel is canvas-scoped (only meaningful while a canvas is
  // mounted), so it doesn't belong in useUIStore alongside doc-level
  // panels (history, comments, assistant).
  const [framesPanelOpen, setFramesPanelOpen] = useState(false);
  const toggleFramesPanel = useCallback(() => setFramesPanelOpen((o) => !o), []);
  const closeFramesPanel = useCallback(() => setFramesPanelOpen(false), []);

  const [toolPanel, setToolPanel] = useState<ToolPanelConfig | null>(null);
  const closeToolPanel = useCallback(() => setToolPanel(null), []);

  // Stable cursor position ref — updated by onCursorMove from CanvasEngine
  // (canvas-space coords). Paste uses this so nodes land under the mouse.
  const cursorRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const handleCursorMove = useCallback((pt: { x: number; y: number }) => {
    cursorRef.current = pt;
  }, []);
  const setPasteRoot = usePasteHandler({
    doc: doc ?? emptyDoc,
    clientId: doc?.clientID ?? 0,
    getCursorPosition: () => cursorRef.current,
    onResolverPaste: handlePastedText,
  });

  useToolShortcut({ selectTool, reset });
  useShortcutsPanelShortcut(() => setShortcutsOpen((o) => !o));
  useSpacePanShortcut({
    onActivate: () => {
      spacePreToolRef.current = activeTool;
      selectTool({ kind: 'hand' });
    },
    onDeactivate: () => {
      if (spacePreToolRef.current) {
        selectTool(spacePreToolRef.current);
        spacePreToolRef.current = null;
      }
    },
  });

  const handlePaneClick = useCallback((position: { x: number; y: number }, frameId: string | null) => {
    if (!doc || activeTool.kind === 'select' || activeTool.kind === 'hand' || activeTool.kind === 'freedraw' || activeTool.kind === 'highlighter' || activeTool.kind === 'eraser' || activeTool.kind === 'connector') return;
    const overrides =
      activeTool.kind === 'shape' ? { shapeKind: activeTool.shapeKind } :
      activeTool.kind === 'sticky' ? { fill: activeTool.stickyColor } :
      activeTool.kind === 'emoji' ? { group: activeTool.ref.group, id: activeTool.ref.id, variant: activeTool.ref.variant } :
      activeTool.kind === 'gif' ? { ...activeTool.data } :
      undefined;
    // Single source of truth for placement size (per tool + shapeKind),
    // shared with the placement ghost. Pill spawns wide so it reads as a
    // pill; frame presets keep their chosen size.
    const effectiveSize = resolvePlacementSize(activeTool);
    // Offset by half the size so the placed node is centered on cursor.
    const placementPos = effectiveSize
      ? { x: position.x - effectiveSize.width / 2, y: position.y - effectiveSize.height / 2 }
      : position;
    // Text nodes have no fixed size — they grow with content.
    const nodeSize = activeTool.kind === 'text' ? null : (effectiveSize && effectiveSize.width > 0 ? effectiveSize : null);
    const newNode = createNode(
      doc,
      activeTool.kind as Parameters<typeof createNode>[1],
      placementPos,
      overrides,
      doc.clientID,
      nodeSize,
      binding,
    );
    // If the click landed inside a frame, attach the new node to it.
    // Convert the canvas-absolute placement position to frame-local coords,
    // set parentId, and append to the frame's childOrder — mirrors what
    // useAutoAttach does on drag-drop.
    if (frameId) {
      const nodes = canvasRoots(doc, binding).nodes;
      const frame = nodes.get(frameId);
      if (frame && frame.get('type') === 'frame' && frame.get('locked') !== true) {
        const framePos = frame.get('position') as { x: number; y: number };
        const localPos = absoluteToFrameLocal(placementPos, framePos);
        doc.transact(() => {
          newNode.set('parentId', frameId);
          newNode.set('position', localPos);
          const frameData = frame.get('data') as Y.Map<unknown>;
          const childOrder = frameData.get('childOrder') as Y.Array<string>;
          const newId = newNode.get('id') as string;
          if (!childOrder.toArray().includes(newId)) childOrder.push([newId]);
        }, doc.clientID);
      }
    }
    setPendingSelectId(newNode.get('id') as string);
    reset();
  }, [doc, activeTool, reset, binding]);

  const isToolActive = activeTool.kind !== 'select' && activeTool.kind !== 'hand';
  const isDrawing = activeTool.kind === 'freedraw' || activeTool.kind === 'highlighter';
  const drawingColor = (activeTool.kind === 'freedraw' || activeTool.kind === 'highlighter') ? activeTool.strokeColor : undefined;
  const drawingWidth = (activeTool.kind === 'freedraw' || activeTool.kind === 'highlighter') ? activeTool.strokeWidth : undefined;
  const drawingOpacity = activeTool.kind === 'highlighter' ? HIGHLIGHTER_OPACITY : undefined;
  const isErasing = activeTool.kind === 'eraser';
  const pointerMode = usePointerMode(activeTool.kind);
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);

  const nodeActions = useNodeActionsImpl(doc ?? null, doc?.clientID ?? 0, setPendingSelectId);

  // All hooks MUST run unconditionally — this useCallback used to live
  // below the synced/doc/awareness early return, which violated the
  // rules of hooks (the callback wasn't declared on the loading
  if (!synced || !doc || !awareness) {
    return (
      <Flex vertical align="center" justify="center" data-testid="canvas-loading" className={styles.loading} role="status">
        <LogoAnimated size={240} loop duration={1200} holdDuration={300} />
      </Flex>
    );
  }

  const onPaneClick = !readOnly && isToolActive ? handlePaneClick : undefined;

  return (
    <CanvasContext.Provider value={{ canvasId: entityId }}>
      <ImperativeProvider>
      <NodeActionsContext.Provider value={readOnly ? null : nodeActions}>
        <div
          ref={(node) => {
            containerRef.current = node;
            if (!readOnly) setPasteRoot(node);
          }}
          className={styles.root}
          data-testid="canvas-root"
          data-tool-active={(!readOnly && isToolActive) || undefined}
        >
          {!readOnly && !embedded && (
            <a href="#canvas-content" className={styles.skipLink}>
              Skip to canvas content
            </a>
          )}
          {!readOnly && !embedded && <Actionbar documentId={entityId} />}
          {user ? (
            <CanvasAwarenessMount
              doc={doc}
              awareness={awareness}
              user={{ id: user.sub, name: user.name }}
              canvasId={entityId}
              readOnly={readOnly}
              onCursorMove={handleCursorMove}
              onPaneClick={onPaneClick}
              onViewportChange={handleViewportChange}
              isDrawing={!readOnly && isDrawing}
              drawingColor={drawingColor}
              drawingWidth={drawingWidth}
              drawingOpacity={drawingOpacity}
              isErasing={!readOnly && isErasing}
              connectorMode={!readOnly && activeTool.kind === 'connector'}
              pointerMode={readOnly ? undefined : pointerMode}
              pendingSelectId={pendingSelectId}
              onOpenFramesPanel={readOnly ? undefined : toggleFramesPanel}
              framesPanelOpen={framesPanelOpen}
            />
          ) : (
            <EmbeddableCanvas
              doc={doc}
              awareness={awareness}
              readOnly={readOnly}
              onCursorMove={handleCursorMove}
              onPaneClick={onPaneClick}
              isErasing={!readOnly && isErasing}
              connectorMode={!readOnly && activeTool.kind === 'connector'}
              pendingSelectId={pendingSelectId}
              onOpenFramesPanel={readOnly ? undefined : toggleFramesPanel}
              framesPanelOpen={framesPanelOpen}
            />
          )}
          {!readOnly && isEmpty && (
            <Flex
              vertical
              align="center"
              gap="xs"
              data-testid="canvas-empty-hint"
              className={styles.emptyHint}
              aria-hidden="true"
            >
              <Text type="secondary">Empty canvas</Text>
              <Text size="xs" type="secondary">
                Click the toolbar to add nodes, or paste content to get started.
              </Text>
            </Flex>
          )}
          {!readOnly && (
            <>
              <ToolPlacementGhost activeTool={activeTool} containerRef={containerRef} zoom={zoom} />
              <Flex align="center" justify="center" className={styles.toolbar} onDoubleClick={e => e.stopPropagation()}>
                <CanvasToolbar activeTool={activeTool} onSelectTool={(tool: ActiveTool) => selectTool(tool)} onOpenToolPanel={setToolPanel} />
              </Flex>
              <div className={styles.toastOverlay} onDoubleClick={e => e.stopPropagation()}>
                <TentativeBatchToast count={tentativeCount} onAcceptAll={acceptAll} />
              </div>
            </>
          )}
        </div>
      </NodeActionsContext.Provider>
      <CanvasShortcutsPanel open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      {toolPanel && (
        <ToolPanel
          open
          title={toolPanel.title}
          hideHeader={toolPanel.hideHeader}
          triggerRef={toolPanel.triggerRef}
          verticalAnchorRef={toolPanel.verticalAnchorRef}
          search={toolPanel.search}
          onClose={closeToolPanel}
        >
          {toolPanel.content}
        </ToolPanel>
      )}
      {/* Frames panel — local state, canvas-scoped. The Drawer mounts
          outside the canvas's CanvasContext.Provider tree but reads
          the same Yjs doc directly. Floating overlay, no mask, so the
          canvas underneath stays interactive. */}
      <Drawer
        open={framesPanelOpen}
        onClose={closeFramesPanel}
        width={320}
        bodyStyle={{ padding: 0 }}
        floating
      >
        <FramesPanel doc={doc ?? null} onClose={closeFramesPanel} />
      </Drawer>
      </ImperativeProvider>
    </CanvasContext.Provider>
  );
}
